import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from 'react';
import {
  AUTH_SESSION_STATUS,
  getAuthSessionState,
  subscribeAuthSession,
} from '../services/authSessionStore';
import { ensureActiveSession } from '../services/session';
import { clearLegacyTokens, startAuthEvents, subscribeAuthChanges } from '../services/browserAuthRuntime';
import {
  getProductEntryPath as resolveProductEntryPath,
  hasBillingAccess as resolveBillingAccess,
  hasPromptOnlyProductAccess as resolvePromptOnlyProductAccess,
  hasProductAccess as resolveProductAccess,
  hasTrialRestrictedProductAccess as resolveTrialRestrictedProductAccess,
} from '../utils/billing';
import { hasRestaurantAccess as resolveRestaurantAccess } from '../utils/restaurant';

const AuthSessionContext = createContext(null);

const useAuthSessionStoreValue = () => {
  const state = useSyncExternalStore(
    subscribeAuthSession,
    getAuthSessionState,
    getAuthSessionState
  );
  const isAuthenticated = Boolean(state.session);
  const session = state.session;
  const status = state.status;

  return useMemo(() => {
    const user = session?.user || null;
    const restaurant = session?.restaurant || null;
    const billing = session?.billing || null;
    const auth = session?.auth || null;
    const products = session?.products || {};
    const isHydrating =
      !session && !state.error && status !== AUTH_SESSION_STATUS.READY;

    return {
      session,
      status,
      isAuthenticated,
      sessionError: state.error,
      retrySession: () => ensureActiveSession({ force: true }),
      isHydrating,
      isReady: status === AUTH_SESSION_STATUS.READY,
      user,
      restaurant,
      billing,
      auth,
      products,
      isAdmin: user?.isAdmin === true,
      isOwner: user?.isOwner === true,
      cheflistAdmin: user?.cheflistAdmin === true,
      atendabotAtendente: user?.atendabotAtendente === true,
      isFoodUser: typeof user?.isFood === 'boolean' ? user.isFood : null,
      hasBillingAccess: resolveBillingAccess(session),
      hasRestaurantAccess: resolveRestaurantAccess(session),
      hasProductAccess: (productKey, options = {}) =>
        resolveProductAccess(session, productKey, options),
      hasPromptOnlyProductAccess: (productKey) =>
        resolvePromptOnlyProductAccess(session, productKey),
      hasTrialRestrictedProductAccess: (productKey) =>
        resolveTrialRestrictedProductAccess(session, productKey),
      getProductEntryPath: (productKey, defaultPath) =>
        resolveProductEntryPath(session, productKey, defaultPath),
    };
  }, [isAuthenticated, session, status, state.error]);
};

export const AuthSessionProvider = ({ children }) => {
  const authSession = useAuthSessionStoreValue();

  useEffect(() => {
    clearLegacyTokens();
    const stop = startAuthEvents();
    const load = () => { void ensureActiveSession({ force: true }).catch(() => {}); };
    const unsubscribe = subscribeAuthChanges((type) => { if (type !== 'logout') load(); });
    load();
    window.addEventListener('online', load);
    return () => { stop(); unsubscribe(); window.removeEventListener('online', load); };
  }, []);

  return (
    <AuthSessionContext.Provider value={authSession}>
      <div key={authSession.session?.auth?.contextId || 'anonymous'} style={{ display: 'contents' }}>{children}</div>
    </AuthSessionContext.Provider>
  );
};

export const useAuthSession = () => {
  const context = useContext(AuthSessionContext);
  const storeValue = useAuthSessionStoreValue();
  return context || storeValue;
};
